import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSubscription } from "../context/SubscriptionContext";
import uiStyles from "../theme/uiStyles";

const plans = [
  {
    id: "monthly",
    label: "Monthly",
    price: "$3",
    period: "/month",
    priceId: process.env.REACT_APP_STRIPE_MONTHLY_PRICE_ID,
    description: "Billed monthly. Cancel anytime.",
    badge: null,
  },
  {
    id: "annual",
    label: "Annual",
    price: "$24",
    period: "/year",
    priceId: process.env.REACT_APP_STRIPE_ANNUAL_PRICE_ID,
    description: "Just $2/month. Save 33%.",
    badge: "Best value",
  },
];

const features = [
  { emoji: "🍽️", text: "Unlimited dish suggestions" },
  { emoji: "📖", text: "Full AI-generated recipes for every dish" },
  { emoji: "🖼️", text: "Dish photos with every suggestion" },
  { emoji: "7️⃣", text: "7-day free trial — cancel anytime" },
];

export default function Upgrade() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { isPremium } = useSubscription();
  const [selected, setSelected] = useState("annual");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubscribe = async () => {
    if (!user) {
      nav("/register", { state: { message: "Create an account to start your free trial." } });
      return;
    }

    const plan = plans.find((p) => p.id === selected);
    if (!plan?.priceId) {
      setError("Plan not configured yet. Please try again later.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await fetch("/api/create-checkout-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          priceId: plan.priceId,
          userId: user.uid,
          email: user.email,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to start checkout.");
      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  if (isPremium) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={{ fontSize: 48, textAlign: "center" }}>🎉</div>
          <h2 style={styles.title}>You're on Premium</h2>
          <p style={styles.subtitle}>
            You have full access to recipes and dish photos.
          </p>
          <button style={uiStyles.primaryBtn} onClick={() => nav("/home")}>
            Back to Home
          </button>
          <button
            style={{ ...uiStyles.secondaryBtn, width: "100%", marginTop: 10, textAlign: "center" }}
            onClick={async () => {
              const res = await fetch("/api/customer-portal", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ userId: user.uid }),
              });
              const data = await res.json();
              if (data.url) window.location.href = data.url;
            }}
          >
            Manage subscription
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={{ fontSize: 48, textAlign: "center" }}>✨</div>
        <h2 style={styles.title}>Unlock NextBite Premium</h2>
        <p style={styles.subtitle}>
          Get recipes and dish photos for every suggestion. 7-day free trial included.
        </p>

        <div style={styles.features}>
          {features.map((f) => (
            <div key={f.text} style={styles.featureRow}>
              <span style={styles.featureEmoji}>{f.emoji}</span>
              <span style={styles.featureText}>{f.text}</span>
            </div>
          ))}
        </div>

        <div style={styles.plans}>
          {plans.map((plan) => (
            <div
              key={plan.id}
              onClick={() => setSelected(plan.id)}
              style={{
                ...styles.planCard,
                ...(selected === plan.id ? styles.planCardSelected : {}),
              }}
            >
              {plan.badge && (
                <span style={styles.badge}>{plan.badge}</span>
              )}
              <div style={styles.planLabel}>{plan.label}</div>
              <div style={styles.planPrice}>
                {plan.price}
                <span style={styles.planPeriod}>{plan.period}</span>
              </div>
              <div style={styles.planDesc}>{plan.description}</div>
            </div>
          ))}
        </div>

        {error && <p style={styles.error}>{error}</p>}

        <button
          style={{ ...uiStyles.primaryBtn, marginTop: 8 }}
          onClick={handleSubscribe}
          disabled={loading}
        >
          {loading ? "Redirecting..." : "Start free trial"}
        </button>

        <p style={styles.fine}>
          Credit card required. You won't be charged until your 7-day trial ends.
          Cancel anytime.
        </p>

        <p
          style={styles.back}
          onClick={() => nav(-1)}
        >
          ← Back
        </p>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px 16px",
    fontFamily: "'Inter', Arial, sans-serif",
  },
  card: {
    width: "100%",
    maxWidth: 440,
    background: "rgba(255,255,255,0.92)",
    borderRadius: 24,
    padding: "36px 28px",
    boxShadow: "0 20px 48px rgba(15,23,42,0.10)",
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  title: {
    margin: 0,
    textAlign: "center",
    fontSize: 24,
    fontWeight: 700,
    color: "#0f172a",
  },
  subtitle: {
    margin: 0,
    textAlign: "center",
    fontSize: 15,
    color: "#64748b",
    lineHeight: 1.6,
  },
  features: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    padding: "4px 0",
  },
  featureRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  featureEmoji: { fontSize: 20 },
  featureText: { fontSize: 14, color: "#334155", fontWeight: 500 },
  plans: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
  },
  planCard: {
    border: "2px solid #e7d8d3",
    borderRadius: 16,
    padding: "16px 12px",
    cursor: "pointer",
    position: "relative",
    transition: "all 0.2s",
    background: "#fff",
  },
  planCardSelected: {
    border: "2px solid #f25f4c",
    background: "#fff5f3",
  },
  badge: {
    position: "absolute",
    top: -10,
    left: "50%",
    transform: "translateX(-50%)",
    background: "linear-gradient(135deg, #ff7a59, #f25f4c)",
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    padding: "2px 10px",
    borderRadius: 999,
    whiteSpace: "nowrap",
  },
  planLabel: { fontSize: 13, fontWeight: 600, color: "#64748b", marginBottom: 4 },
  planPrice: { fontSize: 26, fontWeight: 800, color: "#0f172a" },
  planPeriod: { fontSize: 14, fontWeight: 400, color: "#94a3b8" },
  planDesc: { fontSize: 12, color: "#94a3b8", marginTop: 4 },
  error: { margin: 0, color: "#dc2626", fontSize: 13, textAlign: "center" },
  fine: {
    margin: 0,
    fontSize: 12,
    color: "#94a3b8",
    textAlign: "center",
    lineHeight: 1.6,
  },
  back: {
    margin: 0,
    textAlign: "center",
    fontSize: 14,
    color: "#f25f4c",
    cursor: "pointer",
    fontWeight: 500,
  },
};
